import { useContext } from 'react';
import { FilterContext, ListContext } from '../utils/Contexts';
import { TagPicker } from './TagPicker';

import dayjs from "dayjs";
import Slider from "rc-slider";

export function SearchText({ fieldName, placeholder = "", className = "", disabled = false }) {
    const { filters, setFilters, search } = useContext(FilterContext);
    const initialValue = fieldName in search ? search[fieldName] as string : "";

    return (
        <input
            className={className != "" ? `form-control ${className}` : "form-control"}
            id={fieldName.replace(/[^A-Za-z]/g, '') + "Search"}
            placeholder={placeholder || fieldName}
            value={filters[fieldName] ?? initialValue}
            disabled={disabled}
            onChange={(e) => {
                setFilters({
                    ...filters,
                    [fieldName]: e.target.value,
                });
            }}
            aria-label={fieldName}
        />
    )
}

export function SearchList({ fieldName, listValues = [], className = "", disabled = false }) {
    const { filters, setFilters } = useContext(FilterContext);
    listValues = useContext(ListContext).lists[fieldName] ?? listValues;

    const filterNoQuotes = filters[fieldName] !== undefined ? filters[fieldName].replace(/"/g, "") : null;

    return (
        <select
            className={className != "" ? `form-select-sm ${className}` : "form-select-sm"}
            aria-label={fieldName}
            value={filterNoQuotes ?? fieldName}
            disabled={disabled}
            onChange={(e) => {
                const selectedValue = e.target.value;
                if (selectedValue === fieldName) {
                    // Clear the filter when the default option is selected
                    const newFilters = { ...filters };
                    delete newFilters[fieldName];
                    setFilters(newFilters);
                } else {
                    setFilters({
                        ...filters,
                        [fieldName]: `"${selectedValue}"`,
                    });
                }
            }}
        >
            <option value={fieldName}>{fieldName}</option>
            {listValues.map((val, i) => {
                return (
                    <option key={fieldName.slice(0, 3).toLowerCase() + "_" + i} value={val}>
                        {val}
                    </option>
                );
            })}
        </select>
    )
}

export function SearchDateSlider({ fieldName, minDate, maxDate, className = "", disabled = false }) {
    const { filters, setFilters } = useContext(FilterContext);

    const minValue = fieldName in filters ? filters[fieldName][0] as number : minDate;
    const maxValue = fieldName in filters ? filters[fieldName][1] as number : maxDate;

    return (
        <div className={"my-3 date-slider" + (className != "" ? ` ${className}` : "")}>
            <label className="form-label">{fieldName}</label>
            <div className="px-2 pb-2">
                <Slider
                    range
                    min={minDate}
                    max={maxDate}
                    value={[minValue, maxValue]}
                    className="px-2 pb-2"
                    disabled={disabled}
                    onChange={
                        (value) => {
                            let isDefault = (value[0] === minDate) && (value[1] === maxDate) ? true : false;
                            setFilters({
                                ...filters,
                                [fieldName]: value as [number, number],
                                [fieldName + "_includeEmpty"]: isDefault,
                            });
                        }
                    }
                />
            </div>
            <div className="d-flex justify-content-between">
                <small>
                    {dayjs(minValue).format("MM/DD/YYYY")}
                </small>
                <small>
                    {dayjs(maxValue).format("MM/DD/YYYY")}
                </small>
            </div>
        </div>
    )
}

export function SearchTagFolder({ folderName, tags = [], className = "" }: { folderName: string; tags?: string[]; className?: string }) {
    const { filters, setFilters } = useContext(FilterContext);
    const filterValue = filters[folderName] !== undefined
        ? (filters[folderName] as string).replace(/"/g, '')
        : '';

    return (
        <select
            className={className ? `form-select-sm ${className}` : 'form-select-sm'}
            aria-label={folderName}
            value={filterValue || folderName}
            onChange={(e) => {
                const selected = e.target.value;
                if (selected === folderName) {
                    const { [folderName]: _, ...rest } = filters as any;
                    setFilters(rest);
                } else {
                    setFilters({ ...filters, [folderName]: `"${selected}"` });
                }
            }}
        >
            <option value={folderName}>{folderName}</option>
            {tags.map((tag, i) => (
                <option key={i} value={tag}>{tag}</option>
            ))}
        </select>
    );
}

export function SearchPilotFilter({ className = "" }: { className?: string }) {
    const { filters, setFilters } = useContext(FilterContext);
    return (
        <div className={className ? `d-flex align-items-center ${className}` : 'd-flex align-items-center'}>
            <input
                type="checkbox"
                id="pilot-filter"
                className="me-2"
                checked={!!filters['Pilot']}
                onChange={(e) => {
                    if (e.target.checked) {
                        setFilters({ ...filters, Pilot: true } as any);
                    } else {
                        const { Pilot, ...rest } = filters as any;
                        setFilters(rest);
                    }
                }}
            />
            <label htmlFor="pilot-filter" className="mb-0">Pilot</label>
        </div>
    );
}

export function SearchTagFilter({ availableTags = [], className = "" }: { availableTags?: string[]; className?: string }) {
    const { filters, setFilters } = useContext(FilterContext);
    const selectedTags: string[] = Array.isArray(filters['tags']) ? filters['tags'] as string[] : [];

    return (
        <div className={className}>
            <TagPicker
                id="search-tag-filter"
                selectedTags={selectedTags}
                availableTags={availableTags}
                placeholder="Filter by tags..."
                onChange={(tags) => setFilters({ ...filters, tags: tags.length > 0 ? tags : undefined })}
            />
        </div>
    );
}

export function SearchCheckbox({ fieldName, values = [], className = "", disabled = false }) {
    const { filters, setFilters } = useContext(FilterContext);
    return (
        <div className={className != "" ? `${className}` : ""}>
            <input
                className="mx-2"
                type="checkbox"
                checked={!!filters[fieldName]}
                disabled={disabled}
                aria-label={fieldName}
                onChange={(e) => {
                    setFilters({
                        ...filters,
                        [fieldName]: e.target.checked ? values || e.target.checked : undefined,
                    });
                }}
            />
            <label className={"mt-2"}>{fieldName}</label>
        </div>
    )
}